import React from 'react';
import { CalendarDays, ChevronDown, Clock3, MapPin, Sparkles } from 'lucide-react';

const formatSuggestionDate = (dateKey) => {
  if (!dateKey) return '';
  const date = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return new Intl.DateTimeFormat('ru-RU', {
    weekday: 'short',
    day: 'numeric',
    month: 'long'
  }).format(date);
};

const stopLabel = (count) => {
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return 'выездов';
  if (last === 1) return 'выезд';
  if (last >= 2 && last <= 4) return 'выезда';
  return 'выездов';
};

const getRouteStopsCountClassName = (count) => {
  const normalizedCount = Number(count) || 0;
  if (normalizedCount <= 10) return 'text-emerald-600 dark:text-emerald-300';
  if (normalizedCount <= 15) return 'text-amber-600 dark:text-amber-300';
  return 'text-red-600 dark:text-red-300';
};

const RouteSuggestions = ({ suggestions = [], loading = false, onApply, showWhenEmpty = false }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  if (!loading && suggestions.length === 0 && !showWhenEmpty) return null;

  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-violet-200 bg-violet-50/70 dark:border-violet-900/70 dark:bg-violet-950/20">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        className="flex w-full items-center gap-2 px-3 py-3 text-left text-violet-950 transition-colors hover:bg-violet-100/60 dark:text-violet-100 dark:hover:bg-violet-900/20"
      >
        <Sparkles className="h-4 w-4 shrink-0 text-violet-600 dark:text-violet-300" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">Подходящие варианты выезда</span>
          {(loading || suggestions.length === 0) && <span className="mt-0.5 block text-xs leading-4 text-violet-800/90 dark:text-violet-200/90">
            {loading ? 'Подбираем выезды рядом с этим адресом…' : 'Пока нет подходящих выездов — дату можно выбрать вручную'}
          </span>}
        </span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-violet-600 transition-transform dark:text-violet-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="border-t border-violet-200 px-3 pb-3 pt-2 dark:border-violet-900/70">
          {loading ? (
            <div className="h-16 rounded-lg bg-violet-100/80 shimmer-outline dark:bg-violet-900/30" />
          ) : suggestions.length > 0 ? (
            <div className="space-y-2">
              {suggestions.map((suggestion) => (
                <button
                  key={`${suggestion.date_key}-${suggestion.time_slot}`}
                  type="button"
                  onClick={() => onApply?.(suggestion)}
                  className="w-full rounded-lg border border-violet-200 bg-white px-3 py-2.5 text-left transition-colors hover:border-violet-300 hover:bg-violet-50 focus:outline-none focus:ring-2 focus:ring-violet-400 dark:border-violet-800 dark:bg-gray-900 dark:hover:bg-violet-950/40"
                >
                  <span className="flex items-center justify-between gap-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    <span className="flex min-w-0 items-center gap-1.5 capitalize">
                      <CalendarDays className="h-4 w-4 shrink-0 text-violet-600 dark:text-violet-300" />
                      <span className="truncate">{formatSuggestionDate(suggestion.date_key)}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1 text-violet-700 dark:text-violet-200">
                      <Clock3 className="h-4 w-4" />
                      {suggestion.time_slot}
                    </span>
                  </span>
                  <span className="mt-1 flex items-center gap-1.5 text-xs leading-4 text-gray-600 dark:text-gray-300">
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-violet-500" />
                    Заказ №{suggestion.nearest_order_number} · {suggestion.match_label || `${suggestion.nearest_distance_km} км`}
                  </span>
                  <span className={`mt-0.5 block text-[11px] ${getRouteStopsCountClassName(suggestion.route_stops_count)}`}>
                    В этот день уже {suggestion.route_stops_count} {stopLabel(suggestion.route_stops_count)}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="rounded-lg bg-white/70 px-3 py-2.5 text-xs leading-4 text-gray-600 dark:bg-gray-900/70 dark:text-gray-300">
              В ближайшие дни нет назначенных выездов рядом с этим адресом. Выберите удобные дату и интервал вручную.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RouteSuggestions;
